import { createTheme } from '@mui/material/styles';

export const THEME_MODE_KEY = 'gof_theme_mode';

export const themeModes = {
	LIGHT: 'light',
	DARK: 'dark',
};

export const colorTokens = {
	brandStatic: '#033c66',
	accent: '#fc6403',
	accentHover: '#e55a02',
	imagePanel: '#f8fbff',
	imagePanelBorder: '#d8dde6',
	diagramPanel: '#f8fbff',
	diagramPanelBorder: '#d8dde6',
	darkDiagramPanel: '#D3E0E8',
	darkDiagramPanelBorder: 'rgba(83, 139, 179, 0.52)',
	darkFooter: '#012742',
	darkAccent: '#CF5E34',
	darkAccentHover: '#CF7C5D',
	darkGold: '#CFA734',
	light: {
		page: '#fafbfc',
		surface: '#ffffff',
		surfaceAlt: '#fef5ee',
		primary: '#033c66',
		primaryHover: '#044d80',
		primaryDark: '#022b4a',
		text: '#1f2a37',
		textSecondary: '#4a5f78',
		muted: '#7a8da3',
		border: '#eef0f4',
		borderStrong: '#d8dde6',
		shadow: '0 6px 24px rgba(3, 60, 102, 0.1)',
	},
	dark: {
		page: '#012742',
		surface: '#15354D',
		surfaceAlt: '#033D66',
		primary: '#538BB3',
		primaryHover: '#317DB3',
		primaryDark: '#012742',
		text: '#F2F7FB',
		textSecondary: '#C7D9E6',
		muted: '#9BB8CC',
		border: 'rgba(83, 139, 179, 0.28)',
		borderStrong: 'rgba(207, 167, 52, 0.36)',
		shadow: '0 12px 34px rgba(1, 39, 66, 0.34)',
	},
};

const rgbTokens = {
	light: {
		primary: '3, 60, 102',
		accent: '252, 100, 3',
		surface: '255, 255, 255',
	},
	dark: {
		primary: '83, 139, 179',
		accent: '207, 94, 52',
		surface: '21, 53, 77',
	},
};

export const isThemeMode = (value) => value === themeModes.LIGHT || value === themeModes.DARK;

export const getThemeTokens = (mode) => colorTokens[mode] || colorTokens.light;

export const getCssVariables = (mode) => {
	const palette = getThemeTokens(mode);
	const rgb = rgbTokens[mode] || rgbTokens.light;
	const accent = mode === themeModes.DARK ? colorTokens.darkAccent : colorTokens.accent;
	const accentHover = mode === themeModes.DARK ? colorTokens.darkAccentHover : colorTokens.accentHover;
	const diagramPanel = mode === themeModes.DARK ? colorTokens.darkDiagramPanel : colorTokens.diagramPanel;
	const diagramPanelBorder = mode === themeModes.DARK ? colorTokens.darkDiagramPanelBorder : colorTokens.diagramPanelBorder;

	return {
		'--gof-color-page': palette.page,
		'--gof-color-surface': palette.surface,
		'--gof-color-surface-alt': palette.surfaceAlt,
		'--gof-color-primary': palette.primary,
		'--gof-color-primary-hover': palette.primaryHover,
		'--gof-color-primary-dark': palette.primaryDark,
		'--gof-color-text': palette.text,
		'--gof-color-text-secondary': palette.textSecondary,
		'--gof-color-muted': palette.muted,
		'--gof-color-border': palette.border,
		'--gof-color-border-strong': palette.borderStrong,
		'--gof-color-shadow': palette.shadow,
		'--gof-color-accent': accent,
		'--gof-color-accent-hover': accentHover,
		'--gof-color-accent-soft': mode === themeModes.DARK ? colorTokens.darkGold : colorTokens.accent,
		'--gof-color-brand-static': colorTokens.brandStatic,
		'--gof-color-image-panel': colorTokens.imagePanel,
		'--gof-color-image-panel-border': colorTokens.imagePanelBorder,
		'--gof-color-diagram-panel': diagramPanel,
		'--gof-color-diagram-panel-border': diagramPanelBorder,
		'--gof-color-footer': mode === themeModes.DARK ? colorTokens.darkFooter : colorTokens.brandStatic,
		'--gof-rgb-primary': rgb.primary,
		'--gof-rgb-accent': rgb.accent,
		'--gof-rgb-surface': rgb.surface,
	};
};

export const createGofTheme = (mode) => {
	const palette = getThemeTokens(mode);
	const isDark = mode === themeModes.DARK;

	return createTheme({
		palette: {
			mode,
			primary: {
				main: palette.primary,
				dark: palette.primaryDark,
			},
			secondary: {
				main: isDark ? colorTokens.darkAccent : colorTokens.accent,
				dark: isDark ? colorTokens.darkAccentHover : colorTokens.accentHover,
			},
			background: {
				default: palette.page,
				paper: palette.surface,
			},
			text: {
				primary: palette.text,
				secondary: palette.textSecondary,
			},
			divider: palette.border,
		},
		shape: {
			borderRadius: 8,
		},
		components: {
			MuiCssBaseline: {
				styleOverrides: {
					body: {
						backgroundColor: palette.page,
						color: palette.text,
						transition: 'background-color 0.2s ease, color 0.2s ease',
					},
					'body[data-theme="dark"] a': {
						color: 'inherit',
					},
					'body[data-theme="dark"] .Toastify__toast': {
						background: `${palette.surface} !important`,
						color: `${palette.text} !important`,
						border: `1px solid ${palette.border}`,
					},
					'body[data-theme="dark"] img[src*="/images/patterns/diagrams/"]': {
						backgroundColor: colorTokens.darkDiagramPanel,
						border: `1px solid ${colorTokens.darkDiagramPanelBorder}`,
						borderRadius: '12px',
						boxSizing: 'border-box',
						padding: 16,
					},
					'body[data-theme="dark"] img[src*="/images/home/"], body[data-theme="dark"] img[src*="/images/patterns/collage/"]': {
						backgroundColor: colorTokens.imagePanel,
						border: `1px solid ${colorTokens.imagePanelBorder}`,
						borderRadius: '12px',
						boxSizing: 'border-box',
						padding: 10,
					},
				},
			},
			MuiPaper: {
				styleOverrides: {
					root: {
						backgroundImage: 'none',
					},
				},
			},
			MuiButton: {
				styleOverrides: {
					root: {
						borderRadius: 8,
					},
				},
			},
			MuiTooltip: {
				styleOverrides: {
					tooltip: {
						backgroundColor: isDark ? '#1f2937' : '#263647',
					},
				},
			},
		},
	});
};
